/* eslint-disable react-refresh/only-export-components */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Sentry } from '@/shared/lib/sentry';
import { shortRef } from '@/shared/lib/errors';
import { Button } from '@/shared/components/ui/button';

interface Props {
  children: ReactNode;
  /** Changing it clears the error — pass the route path so navigating away recovers. */
  resetKey?: string;
  /** `page` fills the content area; `panel` fits inside a card. */
  size?: 'page' | 'panel';
}

interface State {
  error: Error | null;
  ref: string | null;
}

function Fallback({ refCode, size, onRetry }: { refCode: string | null; size: 'page' | 'panel'; onRetry: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className={
        size === 'page'
          ? 'mx-auto flex max-w-md flex-col items-center gap-3 px-4 py-16 text-center'
          : 'flex flex-col items-center gap-3 px-4 py-8 text-center'
      }
    >
      <AlertTriangle className="size-6 text-destructive-text" aria-hidden />
      <p className="text-base font-medium">{t('errors.boundary.title')}</p>
      <p className="text-sm text-muted-foreground">{t('errors.boundary.hint')}</p>
      {refCode ? (
        <p className="text-xs text-muted-foreground">
          {t('errors.ref')}: <span className="select-all font-mono">{refCode}</span>
        </p>
      ) : null}
      <Button variant="outline" onClick={onRetry}>
        {t('errors.boundary.reload')}
      </Button>
    </div>
  );
}

/**
 * A render error in one zone shows a message there instead of a white screen,
 * and reaches Sentry with the same code the user sees.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, ref: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const eventId = Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    this.setState({ ref: shortRef(eventId) ?? shortRef(crypto.randomUUID?.()) });
    if (import.meta.env.DEV) console.error(error);
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, ref: null });
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <Fallback
        refCode={this.state.ref}
        size={this.props.size ?? 'page'}
        onRetry={() => (this.props.size === 'panel' ? this.setState({ error: null, ref: null }) : window.location.reload())}
      />
    );
  }
}
